import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import AuthenticatedImage from '../components/AuthenticatedImage';
import { ImageIcon, Download, SortAsc, SortDesc, Printer } from 'lucide-react';
import { toast } from 'sonner';

const ReceiptsGallery = () => {
  const [receipts, setReceipts] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [selectedReceipts, setSelectedReceipts] = useState([]);

  useEffect(() => {
    fetchReceipts();
  }, [sortBy, order]);

  const fetchReceipts = async () => {
    try {
      const response = await api.get(`/receipts/gallery/all?sort_by=${sortBy}&order=${order}`);
      setReceipts(response.data);
    } catch (error) {
      console.error('Failed to fetch receipts:', error);
      toast.error('Failed to load receipts gallery');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    const selectedImages = selectedReceipts.length > 0
      ? receipts.filter(r => selectedReceipts.includes(r.id))
      : receipts;

    // Load images as base64 for printing (auth required)
    const imagesWithData = await Promise.all(
      selectedImages.map(async (receipt) => {
        try {
          const response = await api.get(`/receipts/${receipt.id}/image`, { responseType: 'blob' });
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(response.data);
          });
          return { ...receipt, imageData: base64 };
        } catch (e) {
          return { ...receipt, imageData: null };
        }
      })
    );

    const printWindow = window.open('', '_blank');
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipts - FinScan USA</title>
          <style>
            @page { size: A4; margin: 20mm; }
            body { font-family: Arial, sans-serif; }
            .page { page-break-after: always; padding: 20px; }
            .receipt-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
            .receipt-item { border: 1px solid #ddd; padding: 10px; page-break-inside: avoid; }
            .receipt-item img { width: 100%; height: auto; max-height: 300px; object-fit: contain; }
            .receipt-info { margin-top: 10px; font-size: 12px; }
            .receipt-info p { margin: 4px 0; }
            h1 { text-align: center; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <h1>FinScan USA - Receipts Archive</h1>
          <div class="receipt-grid">
            ${imagesWithData.map((receipt, idx) => `
              <div class="receipt-item">
                ${receipt.imageData ? `<img src="${receipt.imageData}" alt="Receipt ${idx + 1}" />` : '<p>Image unavailable</p>'}
                <div class="receipt-info">
                  <p><strong>Merchant:</strong> ${receipt.merchant}</p>
                  <p><strong>Date:</strong> ${receipt.date}</p>
                  <p><strong>Amount:</strong> $${receipt.amount.toFixed(2)}</p>
                  <p><strong>Category:</strong> ${receipt.category}</p>
                </div>
              </div>
              ${(idx + 1) % 4 === 0 ? '</div><div class="page"><div class="receipt-grid">' : ''}
            `).join('')}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const toggleSelect = (id) => {
    setSelectedReceipts(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-2">
              Receipts Gallery
            </h1>
            <p className="text-lg text-muted-foreground">
              All your receipt photos in one place
            </p>
          </div>
          <button
            onClick={handlePrint}
            data-testid="print-receipts-button"
            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity flex items-center space-x-2"
          >
            <Printer className="w-5 h-5" />
            <span>Print {selectedReceipts.length > 0 ? `(${selectedReceipts.length})` : 'All'}</span>
          </button>
        </div>

        {/* Controls */}
        <div className="mb-6 flex flex-wrap gap-4 items-center">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="sort-select"
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="date" className="bg-[#0f172a]">Date</option>
              <option value="amount" className="bg-[#0f172a]">Amount</option>
              <option value="merchant" className="bg-[#0f172a]">Merchant</option>
            </select>
          </div>

          <button
            onClick={() => setOrder(order === 'asc' ? 'desc' : 'asc')}
            data-testid="order-toggle"
            className="p-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
          >
            {order === 'asc' ? <SortAsc className="w-5 h-5" /> : <SortDesc className="w-5 h-5" />}
          </button>

          {selectedReceipts.length > 0 && (
            <button
              onClick={() => setSelectedReceipts([])}
              className="text-sm text-indigo-400 hover:text-indigo-300"
            >
              Clear selection ({selectedReceipts.length})
            </button>
          )}
        </div>

        {/* Gallery Grid */}
        {receipts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {receipts.map((receipt) => (
              <div
                key={receipt.id}
                onClick={() => toggleSelect(receipt.id)}
                className={`relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                  selectedReceipts.includes(receipt.id)
                    ? 'border-indigo-500 ring-2 ring-indigo-500/50'
                    : 'border-white/10 hover:border-white/30'
                }`}
                data-testid={`receipt-${receipt.id}`}
              >
                <div className="aspect-[3/4] bg-white/5">
                  <AuthenticatedImage
                    receiptId={receipt.id}
                    alt={receipt.merchant}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <p className="font-semibold text-lg">{receipt.merchant}</p>
                    <p className="text-sm">${receipt.amount.toFixed(2)} • {receipt.date}</p>
                    <p className="text-xs text-gray-300">{receipt.category}</p>
                  </div>
                </div>
                {selectedReceipts.includes(receipt.id) && (
                  <div className="absolute top-2 right-2 w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center text-muted-foreground bg-white/5 border border-white/10 rounded-xl">
            <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg">No receipts with photos yet</p>
            <p className="text-sm mt-2">Scan receipts to see them here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceiptsGallery;
